/**
 * SignupPromptModal — CTA modal shown when a guest tries to use a locked
 * feature (posting a community deal, uploading a receipt, opening a
 * premium tab, etc.). Kept intentionally friendly: highlights the 14-day
 * All-Access trial + one-click sign up.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, Check, Camera, Utensils, DollarSign, Calendar } from "lucide-react";
import { Button } from "./ui/button";

const FEATURES = [
  { icon: Utensils, text: "AI meal plans, personalized to your household" },
  { icon: DollarSign, text: "Weekly deal alerts on items you actually buy" },
  { icon: Camera, text: "Receipt scanning + budget insights" },
  { icon: Calendar, text: "14-day All-Access trial. Cancel anytime." },
];

export default function SignupPromptModal({
  open,
  onClose,
  headline = "Unlock this feature",
  subhead = "Start your 14-day free All-Access trial — no card required.",
}) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="signup-prompt-overlay"
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="signup-prompt-modal"
      >
        <div className="relative bg-gradient-to-br from-emerald-500 to-green-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/15 rounded-full"
            data-testid="signup-prompt-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5" />
            <span className="text-xs font-bold tracking-wider uppercase opacity-90">
              14-Day Free Trial
            </span>
          </div>
          <h2 className="text-xl font-bold mb-1" data-testid="signup-prompt-headline">
            {headline}
          </h2>
          <p className="text-sm text-white/90">{subhead}</p>
        </div>

        <div className="p-6 space-y-3">
          {FEATURES.map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                <Icon className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex-1 pt-1">
                <div className="flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 text-emerald-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{text}</p>
                </div>
              </div>
            </div>
          ))}

          <div className="pt-2 space-y-2">
            <Button
              onClick={() => {
                onClose();
                navigate("/auth?mode=signup");
              }}
              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold py-6 text-sm"
              data-testid="signup-prompt-start-trial"
            >
              Start My 14-Day Free Trial
            </Button>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/auth?mode=login");
              }}
              className="w-full text-xs text-gray-500 hover:text-emerald-700 py-2"
              data-testid="signup-prompt-login"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
